"use client"

import { useState, useMemo, useEffect, useRef, useCallback, type FormEvent } from "react"
import Link from "next/link"
import { useSearchParams } from "next/navigation"
import { useTranslations } from "next-intl"
import { Search, X, Heart, SearchX, UtensilsCrossed, Navigation } from "lucide-react"
import { LocaleSwitcher } from "@/components/ui/LocaleSwitcher"
import { MerchantCard } from "@/components/marketplace/MerchantCard"
import { CategoryFilter } from "@/components/marketplace/CategoryFilter"
import { CityPicker } from "@/components/marketplace/CityPicker"
import { AccountMenu, type AccountMenuAccount } from "@/components/consumer/AccountMenu"
import { ConsumerBottomNav } from "@/components/consumer/ConsumerBottomNav"
import { searchMarketplace } from "@/app/actions/marketplaceSearchActions"
import {
  toggleFavorite as toggleFavoriteAccount,
  mergeFavorites as mergeFavoritesAccount,
} from "@/app/actions/dinerFavoriteActions"
import { useFavorites } from "@/lib/marketplace/useFavorites"
import {
  makeTracker,
  getSessionId,
  makeOnceGate,
  makeBeaconSender,
  type Tracker,
  type OnceGate,
} from "@/lib/marketplace/track"
import {
  applyFilters,
  filtersToSearchParams,
  parseFilters,
  EMPTY_FILTERS,
  type MarketplaceFilters,
} from "@/lib/marketplace/filters"
import { distanceMiles, type LatLng } from "@/lib/geo/haversine"
import type { MarketplaceMerchant } from "@/lib/marketplace/types"
import type { MarketplaceCuisine } from "@/lib/marketplace/constants"
import { brandAvatarUrl } from "@/lib/branding/brandIcon"

const GRADIENT = "linear-gradient(90deg,#D7263D,#F46B35)"

type Props = {
  merchants: MarketplaceMerchant[]
  /** Filtros leídos de la URL en el servidor: el primer HTML ya llega filtrado. */
  initialFilters: MarketplaceFilters
  /**
   * F1b: prefijo de los enlaces al storefront ("/order" en useredy.com, "" en el
   * dominio consumer, donde /{slug} ES la forma canónica). Lo resuelve el
   * servidor por Host — el cliente no puede: en el navegador `location.host` sí
   * existe, pero leerlo en render rompería la hidratación del HTML servido.
   */
  linkBase?: string
  /** Sesión del comensal (spec §4.1), decidida por el servidor: null = sin sesión. */
  account: AccountMenuAccount | null
  /** uuid de la cuenta con sesión (no es dato sensible); undefined = sin
   *  sesión. Ancla la marca durable de merge de useFavorites (revisión I2). */
  accountId?: string
  /** Favoritos de la cuenta, leídos en el servidor; undefined = sin sesión (useFavorites se queda con el localStorage). */
  favoriteIds?: string[]
  /** Home del dominio actual, para la barra inferior (spec §4.7). */
  homeHref: string
}

export function MarketplaceClient({
  merchants,
  initialFilters,
  linkBase = "/order",
  account,
  accountId,
  favoriteIds,
  homeHref,
}: Props) {
  const t = useTranslations("marketplace")
  // cuisine/city/open viven en la URL (fuente única de verdad): los chips de
  // cocina son <Link> crawleables, y un enlace compartido abre ya filtrado.
  // q se teclea en estado local (respuesta inmediata) y se refleja a la URL con
  // history.replaceState — igual que el directorio del superadmin: sin volver
  // al servidor en cada tecla, sin llenar el historial de basura.
  const searchParams = useSearchParams()
  const urlFilters = useMemo(
    () => parseFilters(Object.fromEntries(searchParams.entries())),
    [searchParams],
  )
  const [search, setSearch] = useState(initialFilters.q)
  const [searchFocused, setSearchFocused] = useState(false)
  const [favoritesOnly, setFavoritesOnly] = useState(false)
  // Resultado del server search etiquetado con la query que lo produjo: los
  // ids de una búsqueda vieja se ignoran al derivar, sin setState síncrono en
  // el effect (lint react-hooks/set-state-in-effect).
  const [serverMatch, setServerMatch] = useState<{ q: string; ids: Set<string> } | null>(null)
  const { favorites, toggle: toggleFavorite } = useFavorites({
    initial: favoriteIds,
    accountId,
    onToggle: favoriteIds ? (id) => { void toggleFavoriteAccount(id) } : undefined,
    // I2 (revisión): a diferencia de onToggle, este SÍ se espera — el hook
    // necesita saber ok/dropped para escribir su marca durable y limpiar los
    // ids que el servidor reportó como inexistentes (ver useFavorites.ts).
    onMerge: favoriteIds ? mergeFavoritesAccount : undefined,
  })

  // F3 — "Cerca de ti". PRIVACIDAD (decisión cerrada del plan): la posición del
  // visitante vive SOLO en este estado de React, en memoria del navegador —
  // jamás va a la URL, a un cookie ni a ningún endpoint. El server ya mandó las
  // coords públicas de los negocios y la distancia se calcula AQUÍ. El único
  // dato que puede subir es el ZIP que el visitante teclea a mano como
  // fallback, y va por POST body (nunca query param) a /api/geo/zcta.
  const [origin, setOrigin] = useState<LatLng | null>(null)
  const [geoDenied, setGeoDenied] = useState(false)
  const [zipInput, setZipInput] = useState("")
  const [zipStatus, setZipStatus] = useState<"idle" | "loading" | "failed">("idle")
  const geoInFlight = useRef(false)

  const filters: MarketplaceFilters = useMemo(
    () => ({ ...urlFilters, q: search }),
    [urlFilters, search],
  )

  /** Escribe los filtros a la URL sin navegación de servidor (shallow). */
  const replaceUrl = useCallback((next: MarketplaceFilters) => {
    const qs = filtersToSearchParams(next).toString()
    window.history.replaceState(
      null,
      "",
      qs ? `${window.location.pathname}?${qs}` : window.location.pathname,
    )
  }, [])

  /** Filtros vigentes leídos de la URL REAL al momento de escribir — los
   *  callbacks diferidos (debounce) no pueden fiarse de un closure viejo. */
  const currentUrlFilters = () =>
    parseFilters(Object.fromEntries(new URLSearchParams(window.location.search).entries()))

  const qDebounce = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(
    () => () => {
      if (qDebounce.current) clearTimeout(qDebounce.current)
    },
    [],
  )

  // ── Medición (F5): tracker anónimo best-effort ─────────────────────────────
  // Cola con flush a los 4s / 20 eventos / pagehide (sendBeacon). Todo lazy y
  // solo desde handlers/effects — jamás durante el render (SSR incluido).
  const trackerRef = useRef<Tracker | null>(null)
  const impressionGateRef = useRef<OnceGate | null>(null)
  const impressionObserverRef = useRef<IntersectionObserver | null>(null)

  const getTracker = useCallback((): Tracker => {
    if (!trackerRef.current) {
      trackerRef.current = makeTracker({ sessionId: getSessionId(), send: makeBeaconSender() })
    }
    return trackerRef.current
  }, [])

  useEffect(() => {
    const onPageHide = () => trackerRef.current?.flush()
    window.addEventListener("pagehide", onPageHide)
    const observer = impressionObserverRef
    return () => {
      window.removeEventListener("pagehide", onPageHide)
      // Al navegar (SPA) el componente se desmonta pero la pestaña sigue viva:
      // drenar aquí garantiza que el card_click del Link sí se mande.
      trackerRef.current?.flush()
      observer.current?.disconnect()
      observer.current = null
    }
  }, [])

  /** Impresión por card: threshold 0.5, UNA vez por card por sesión (gate en
   *  sessionStorage — una recarga no vuelve a contar las mismas cards). */
  const observeCard = useCallback(
    (el: HTMLDivElement | null) => {
      if (!el || typeof IntersectionObserver === "undefined") return
      if (!impressionObserverRef.current) {
        impressionObserverRef.current = new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (!entry.isIntersecting) continue
              const id = (entry.target as HTMLElement).dataset.locationId
              if (!id) continue
              const gate = (impressionGateRef.current ??= makeOnceGate())
              if (!gate.has(id)) {
                gate.add(id)
                getTracker().track({ event: "impression", location_id: id, placement: "grid" })
              }
              impressionObserverRef.current?.unobserve(entry.target)
            }
          },
          { threshold: 0.5 },
        )
      }
      impressionObserverRef.current.observe(el)
    },
    [getTracker],
  )

  /** Click-through a un merchant. En bubble (no capture): el corazón de
   *  favorito hace stopPropagation y NO cuenta como click a la card. */
  const trackCardClick = useCallback(
    (locationId: string, placement: "grid" | "suggestion") => {
      getTracker().track({ event: "card_click", location_id: locationId, placement })
    },
    [getTracker],
  )

  // Cambio de filtros (chips de cocina vía Link, ciudad, abierto ahora): un
  // evento `filter` con el estado resultante. La firma evita disparos por
  // renders ajenos (p. ej. q escribiéndose a la URL con replaceState).
  const filterSig = `${urlFilters.cuisine ?? ""}|${urlFilters.city ?? ""}|${urlFilters.open ? 1 : 0}`
  const prevFilterSig = useRef<string | null>(null)
  useEffect(() => {
    if (prevFilterSig.current === filterSig) return
    const isFirst = prevFilterSig.current === null
    prevFilterSig.current = filterSig
    if (isFirst) return // el estado inicial de la URL no es una interacción
    getTracker().track({
      event: "filter",
      meta: { cuisine: urlFilters.cuisine, city: urlFilters.city, open: urlFilters.open },
    })
  }, [filterSig, urlFilters, getTracker])

  function onSearchChange(value: string) {
    setSearch(value)
    if (qDebounce.current) clearTimeout(qDebounce.current)
    qDebounce.current = setTimeout(() => {
      replaceUrl({ ...currentUrlFilters(), q: value })
      // Medición: solo la query ASENTADA (mismo debounce que la URL) — cada
      // tecla re-arma el timer, así que no se cuenta el tipeo intermedio.
      const q = value.trim()
      if (q.length >= 2) {
        getTracker().track({ event: "search", placement: "hero", meta: { q: q.slice(0, 200) } })
      }
    }, 300)
  }

  function setCity(city: string | null) {
    replaceUrl({ ...currentUrlFilters(), q: search, city })
  }

  function toggleOpenNow() {
    replaceUrl({ ...currentUrlFilters(), q: search, open: !urlFilters.open })
  }

  const nearActive = urlFilters.sort === "near"

  function toggleNear() {
    const turningOn = !nearActive
    // La URL lleva SOLO `sort=near` — jamás coordenadas.
    replaceUrl({ ...currentUrlFilters(), q: search, sort: turningOn ? "near" : null })
    // Re-encender tras una negación reintenta la geolocalización (el effect de
    // abajo vuelve a disparar); quizá el visitante ya dio el permiso.
    if (turningOn) setGeoDenied(false)
  }

  // Pide la posición cuando el orden por cercanía está activo y aún no hay
  // origen (incluye llegar con ?sort=near en un enlace compartido / recarga).
  // Los setState viven en los CALLBACKS asíncronos de getCurrentPosition, no en
  // el cuerpo del effect (lint react-hooks/set-state-in-effect).
  useEffect(() => {
    if (!nearActive || origin || geoDenied || geoInFlight.current) return
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      // API ausente (rarísimo): degradar al fallback de ZIP, asíncrono a propósito.
      const t = setTimeout(() => setGeoDenied(true), 0)
      return () => clearTimeout(t)
    }
    geoInFlight.current = true
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        geoInFlight.current = false
        setOrigin({ lat: pos.coords.latitude, lng: pos.coords.longitude })
      },
      () => {
        geoInFlight.current = false
        setGeoDenied(true)
      },
      { timeout: 8000, maximumAge: 300_000 },
    )
  }, [nearActive, origin, geoDenied])

  async function submitZip(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const zip = zipInput.trim()
    if (!/^\d{5}$/.test(zip)) {
      setZipStatus("failed")
      return
    }
    setZipStatus("loading")
    try {
      // POST body a propósito: un ?zip= en la URL queda en access logs,
      // analytics y Referer; el body no. La respuesta es el centroide PÚBLICO
      // del ZCTA — suficiente para ORDENAR (la UI imprime bandas, no precisión).
      const res = await fetch("/api/geo/zcta", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ zip }),
      })
      if (!res.ok) {
        setZipStatus("failed")
        return
      }
      const data: { lat?: number; lng?: number; found?: boolean } = await res.json()
      if (typeof data.lat === "number" && typeof data.lng === "number") {
        setOrigin({ lat: data.lat, lng: data.lng })
        setZipStatus("idle")
      } else {
        setZipStatus("failed")
      }
    } catch {
      setZipStatus("failed")
    }
  }

  /** Path del directorio en ESTE host: "/order" en useredy.com, "/" en el
   *  dominio consumer (donde /order redirige 308 a la raíz). Los chips son la
   *  ruta más caliente del directorio: enlazarlos a la forma que redirige haría
   *  que cada filtro costara un salto extra. */
  const dirPath = linkBase === "" ? "/" : "/order"

  /** Href de un chip de cocina preservando el resto de filtros activos. */
  const hrefFor = useCallback(
    (cuisine: MarketplaceCuisine | null) => {
      const qs = filtersToSearchParams({ ...filters, cuisine }).toString()
      return qs ? `${dirPath}?${qs}` : dirPath
    },
    [filters, dirPath],
  )

  // Typo-tolerant server search (pg_trgm), debounced. Se une con la búsqueda
  // instantánea en memoria; fail-soft (queda null si el RPC no está aplicado).
  useEffect(() => {
    const q = search.trim()
    if (q.length < 2) return
    let cancelled = false
    const t = setTimeout(() => {
      searchMarketplace(q)
        .then((ids) => {
          if (!cancelled) setServerMatch({ q, ids: new Set(ids) })
        })
        .catch(() => {
          // fail-soft: sin match del server, la búsqueda en memoria sigue viva
        })
    }, 250)
    return () => {
      cancelled = true
      clearTimeout(t)
    }
  }, [search])

  const serverMatchIds =
    serverMatch && serverMatch.q === search.trim() ? serverMatch.ids : undefined

  const availableCities = useMemo(
    () => [...new Set(merchants.map((m) => m.city).filter(Boolean))].sort() as string[],
    [merchants],
  )
  const cityCount = availableCities.length

  // Distancia en millas por merchant, calculada EN EL NAVEGADOR (privacidad:
  // el server nunca ve la posición del visitante). Solo existe con origen
  // conocido; los merchants sin coords quedan fuera del mapa.
  const distances = useMemo(() => {
    if (!nearActive || !origin) return null
    const map = new Map<string, number>()
    for (const m of merchants) {
      if (m.lat !== null && m.lng !== null) {
        map.set(m.id, distanceMiles(origin, { lat: m.lat, lng: m.lng }))
      }
    }
    return map
  }, [merchants, nearActive, origin])

  const filtered = useMemo(() => {
    const list = applyFilters(merchants, filters, serverMatchIds)
    if (distances) {
      // Cercanía manda; sin coords al final; empate → el orden estable de
      // siempre (abiertos primero, luego alfabético).
      return [...list].sort((a, b) => {
        const da = distances.get(a.id) ?? Infinity
        const db = distances.get(b.id) ?? Infinity
        // Comparación explícita: Infinity - Infinity sería NaN y rompería el sort.
        if (da !== db) return da < db ? -1 : 1
        return Number(b.isOpen) - Number(a.isOpen) || a.name.localeCompare(b.name)
      })
    }
    // Open restaurants first, then alphabetical (the SSR query already ordered by name).
    return [...list].sort(
      (a, b) => Number(b.isOpen) - Number(a.isOpen) || a.name.localeCompare(b.name),
    )
  }, [merchants, filters, serverMatchIds, distances])

  const visible = useMemo(
    () => (favoritesOnly ? filtered.filter((m) => favorites.has(m.id)) : filtered),
    [filtered, favoritesOnly, favorites],
  )

  const searchQuery = search.trim().toLowerCase()

  // Deduped pool of every dish name across the loaded restaurants (for autocomplete).
  const allDishes = useMemo(() => {
    const seen = new Set<string>()
    const out: string[] = []
    for (const m of merchants) {
      for (const d of m.dish_names) {
        const key = d.toLowerCase()
        if (!seen.has(key)) {
          seen.add(key)
          out.push(d)
        }
      }
    }
    return out
  }, [merchants])

  // Suggestions: matching dishes + restaurants (prefix matches ranked first).
  const suggestions = useMemo(() => {
    const q = searchQuery
    if (q.length < 2) return { dishes: [] as string[], restaurants: [] as MarketplaceMerchant[] }
    const rank = (s: string) => (s.toLowerCase().startsWith(q) ? 0 : 1)
    const dishes = allDishes
      .filter((d) => d.toLowerCase().includes(q))
      .sort((a, b) => rank(a) - rank(b))
      .slice(0, 6)
    const restaurants = merchants
      .filter((m) => m.name.toLowerCase().includes(q))
      .sort((a, b) => rank(a.name) - rank(b.name))
      .slice(0, 4)
    return { dishes, restaurants }
  }, [allDishes, merchants, searchQuery])

  const showSuggestions =
    searchFocused &&
    searchQuery.length >= 2 &&
    (suggestions.dishes.length > 0 || suggestions.restaurants.length > 0)

  const hasActiveFilters = !!(
    filters.city ||
    filters.cuisine ||
    search ||
    filters.open ||
    filters.sort ||
    favoritesOnly
  )

  function clearAll() {
    if (qDebounce.current) clearTimeout(qDebounce.current)
    setSearch("")
    setFavoritesOnly(false)
    replaceUrl(EMPTY_FILTERS)
  }

  return (
    // M4: el padding para no tapar el contenido con la barra fija de móvil
    // (ConsumerBottomNav, más abajo) vive en el <main> de la página — este
    // div YA NO lo trae porque MarketplaceFooter se renderiza DESPUÉS de
    // este componente (app/order/(marketplace)/page.tsx), fuera de él: el
    // padding puesto aquí dejaba el hueco ANTES del footer en vez de
    // después, y la barra fija tapaba el footer en móvil.
    <div>
      {/* ─── DARK HERO with functional search + video background ─── */}
      <section className="relative px-4 py-14 sm:py-16 overflow-hidden" style={{ background: "#0D0D0D" }}>
        {/* Video background — same technique as the marketing hero (HeroSection.tsx) */}
        <video
          autoPlay
          muted
          loop
          playsInline
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover pointer-events-none"
        >
          <source src="/marketplace-hero.mp4" type="video/mp4" />
        </video>
        {/* Dark gradient overlay — like marketing's hero overlay, tuned dark so the
            white headline + search stay readable while the video shows through. */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(180deg, rgba(13,13,13,0.55) 0%, rgba(13,13,13,0.4) 48%, rgba(13,13,13,0.68) 100%)",
          }}
        />

        {/* Idioma + cuenta — reusa el LocaleSwitcher del microsite (cookie
            redy-locale + router.refresh() para que el servidor re-lea la
            cookie); AccountMenu resuelve "Entrar" vs. el menú de la cuenta
            (spec §4.1), en modo oscuro por el hero. */}
        <div className="absolute top-4 right-4 z-20 flex items-center gap-2">
          <LocaleSwitcher
            variant="toggle"
            className="text-[13px] font-semibold px-3 py-1.5 rounded-full bg-white/15 backdrop-blur-sm text-white hover:bg-white/25 transition-colors"
          />
          <AccountMenu account={account} onDark />
        </div>

        <div className="relative z-10 max-w-2xl mx-auto flex flex-col items-center text-center">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/redy-order-logo.png"
            alt="Redy Order"
            className="h-11 sm:h-14 w-auto mb-4"
          />

          <h1
            className="text-[30px] sm:text-[46px] font-black text-white leading-[1.06] max-w-xl"
            style={{ letterSpacing: "-0.03em", textShadow: "0 2px 18px rgba(0,0,0,0.4)" }}
          >
            {t("hero.titleLead")}{" "}
            <span
              style={{
                background: GRADIENT,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              {t("hero.titleAccent")}
            </span>
            .
          </h1>

          {/* Search — the hero's centerpiece */}
          <div className="relative w-full max-w-xl mt-7">
            <Search
              className="absolute left-4 top-1/2 -translate-y-1/2 w-[19px] h-[19px] text-[#9A968F] z-10"
              aria-hidden
            />
            <input
              type="text"
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
              onKeyDown={(e) => {
                if (e.key === "Escape") setSearchFocused(false)
              }}
              placeholder={t("hero.searchPlaceholder")}
              aria-label={t("hero.searchAria")}
              className="relative w-full py-3.5 pl-12 pr-11 rounded-2xl bg-white text-base text-[#1A1A1A] placeholder:text-[#9A968F] shadow-[0_8px_30px_rgba(0,0,0,0.18)] focus:outline-none focus:ring-2 focus:ring-[#F46B35]/60"
            />
            {search && (
              <button
                onClick={() => onSearchChange("")}
                aria-label={t("hero.clearSearch")}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 z-10 text-[#9A968F] hover:text-[#1A1A1A]"
              >
                <X className="w-[18px] h-[18px]" />
              </button>
            )}

            {/* Autocomplete dropdown — dishes + restaurants */}
            {showSuggestions && (
              // onMouseDown preventDefault keeps the input focused so clicks register
              // before blur closes the panel.
              <div
                onMouseDown={(e) => e.preventDefault()}
                className="absolute left-0 right-0 top-full mt-2 z-30 overflow-hidden rounded-2xl border border-[#E8E4DF] bg-white text-left shadow-[0_16px_44px_rgba(0,0,0,0.28)]"
              >
                {suggestions.dishes.length > 0 && (
                  <div className="py-1.5">
                    <div className="px-4 py-1 text-[11px] font-semibold uppercase tracking-wide text-[#9A968F]">
                      {t("hero.suggestionsDishes")}
                    </div>
                    {suggestions.dishes.map((d) => (
                      <button
                        key={d}
                        type="button"
                        onClick={() => {
                          onSearchChange(d)
                          setSearchFocused(false)
                        }}
                        className="flex w-full items-center gap-2.5 px-4 py-2 hover:bg-[#FBF9F6]"
                      >
                        <Search className="w-4 h-4 flex-shrink-0 text-[#9A968F]" aria-hidden />
                        <span className="truncate text-[14px] text-[#1A1A1A]">{d}</span>
                      </button>
                    ))}
                  </div>
                )}
                {suggestions.restaurants.length > 0 && (
                  <div className="border-t border-[#F0EDE8] py-1.5">
                    <div className="px-4 py-1 text-[11px] font-semibold uppercase tracking-wide text-[#9A968F]">
                      {t("hero.suggestionsRestaurants")}
                    </div>
                    {suggestions.restaurants.map((m) => (
                      <Link
                        key={m.id}
                        href={`${linkBase}/${m.slug}`}
                        onClick={() => trackCardClick(m.id, "suggestion")}
                        className="flex w-full items-center gap-2.5 px-4 py-2 hover:bg-[#FBF9F6]"
                      >
                        <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg bg-[#F4F0EA]">
                          {brandAvatarUrl(m) ? (
                            // eslint-disable-next-line @next/next/no-img-element
                            <img src={brandAvatarUrl(m)!} alt="" className="h-full w-full object-cover" />
                          ) : (
                            <span className="text-[12px] font-bold text-[#D7263D]">
                              {m.name[0]?.toUpperCase()}
                            </span>
                          )}
                        </span>
                        <span className="truncate text-[14px] text-[#1A1A1A]">{m.name}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <p
            className="text-white/65 text-[13px] mt-4"
            style={{ textShadow: "0 1px 10px rgba(0,0,0,0.6)" }}
          >
            {t("hero.tagline")}
          </p>

          {merchants.length > 0 && (
            <p
              className="text-white/55 text-[13px] mt-1.5"
              style={{ textShadow: "0 1px 10px rgba(0,0,0,0.6)" }}
            >
              {t("hero.merchantCount", { count: merchants.length })}
              {cityCount > 0 && <> · {t("hero.cityCount", { count: cityCount })}</>}
            </p>
          )}

          <Link
            href="/signup"
            className="mt-6 inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-[13px] font-semibold text-white/80 hover:border-white/40 hover:text-white transition-colors"
          >
            {t("hero.ownerCta")} <span style={{ color: "#F46B35" }}>→</span>
          </Link>
        </div>
      </section>

      {/* ─── LIGHT BODY ─── */}
      <section
        className="px-4 lg:px-8 py-8 max-w-7xl mx-auto"
        style={{ background: "#FBF9F6" }}
      >
        {merchants.length === 0 ? (
          <div className="py-24 text-center flex flex-col items-center">
            <UtensilsCrossed className="w-10 h-10 text-[#C5C0B8] mb-3" strokeWidth={1.5} aria-hidden />
            <p className="text-[#1A1A1A] text-sm font-semibold">{t("empty.noneTitle")}</p>
            <p className="text-[#7A7A7A] text-[13px] mt-1">{t("empty.noneBody")}</p>
          </div>
        ) : (
          <>
            {/* Sticky filter bar */}
            <div className="sticky top-0 z-20 -mx-4 lg:-mx-8 px-4 lg:px-8 py-3 mb-6 bg-[#FBF9F6]/92 backdrop-blur-md border-b border-[#EDE9E2]">
              <div className="flex items-center gap-3">
                <div className="flex-1 overflow-hidden">
                  <CategoryFilter selected={urlFilters.cuisine} hrefFor={hrefFor} />
                </div>
                <button
                  onClick={toggleNear}
                  aria-pressed={nearActive}
                  className={[
                    "flex-shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[13px] font-semibold transition-all border",
                    nearActive
                      ? "border-[#D7263D] bg-[#fff5f6] text-[#D7263D]"
                      : "border-[#E8E4DF] bg-white text-[#7A7A7A] hover:border-[#D7263D] hover:text-[#D7263D]",
                  ].join(" ")}
                >
                  <Navigation
                    className="w-[14px] h-[14px]"
                    fill={nearActive ? "#D7263D" : "none"}
                    strokeWidth={2}
                  />
                  {t("filters.nearMe")}
                </button>
                <button
                  onClick={toggleOpenNow}
                  aria-pressed={urlFilters.open}
                  className={[
                    "flex-shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[13px] font-semibold transition-all border",
                    urlFilters.open
                      ? "border-[#16a34a] bg-[#f0fdf4] text-[#16a34a]"
                      : "border-[#E8E4DF] bg-white text-[#7A7A7A] hover:border-[#D7263D] hover:text-[#D7263D]",
                  ].join(" ")}
                >
                  <span
                    className="w-[6px] h-[6px] rounded-full"
                    style={{
                      background: urlFilters.open ? "#16a34a" : "#9CA3AF",
                      boxShadow: urlFilters.open ? "0 0 0 3px rgba(22,163,74,0.15)" : undefined,
                    }}
                  />
                  {t("filters.openNow")}
                </button>
                <button
                  onClick={() => setFavoritesOnly((v) => !v)}
                  aria-pressed={favoritesOnly}
                  className={[
                    "flex-shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[13px] font-semibold transition-all border",
                    favoritesOnly
                      ? "border-[#D7263D] bg-[#fff5f6] text-[#D7263D]"
                      : "border-[#E8E4DF] bg-white text-[#7A7A7A] hover:border-[#D7263D] hover:text-[#D7263D]",
                  ].join(" ")}
                >
                  <Heart
                    className="w-[14px] h-[14px]"
                    fill={favoritesOnly ? "#D7263D" : "none"}
                    strokeWidth={2}
                  />
                  {t("filters.favorites")}
                  {favorites.size > 0 && (
                    <span className="text-[11px] opacity-70">{favorites.size}</span>
                  )}
                </button>
                <CityPicker
                  cities={availableCities}
                  selected={urlFilters.city}
                  onChange={setCity}
                />
              </div>

              {/* F3 — estado de "Cerca de ti": detectando, o fallback de ZIP si
                  el navegador negó la posición. El ZIP viaja por POST body. */}
              {nearActive && !origin && (
                <div className="mt-2.5">
                  {!geoDenied ? (
                    <span className="text-[12px] text-[#7A7A7A]">
                      {t("filters.detectingLocation")}
                    </span>
                  ) : (
                    <form onSubmit={submitZip} className="flex flex-wrap items-center gap-2">
                      <span className="text-[12px] text-[#7A7A7A]">
                        {t("filters.geoFallbackHint")}
                      </span>
                      <input
                        value={zipInput}
                        onChange={(e) => {
                          setZipInput(e.target.value.replace(/\D/g, "").slice(0, 5))
                          setZipStatus("idle")
                        }}
                        inputMode="numeric"
                        autoComplete="postal-code"
                        placeholder={t("filters.zipPlaceholder")}
                        aria-label={t("filters.zipAria")}
                        className="w-28 bg-white border border-[#E8E4DF] rounded-xl px-3 py-1.5 text-base text-[#1A1A1A] placeholder:text-[#9A968F] focus:outline-none focus:border-[#D7263D]"
                      />
                      <button
                        type="submit"
                        disabled={zipStatus === "loading" || zipInput.length !== 5}
                        className="rounded-xl px-3.5 py-1.5 text-[13px] font-semibold text-white transition-opacity disabled:opacity-40"
                        style={{ background: GRADIENT }}
                      >
                        {t("filters.zipApply")}
                      </button>
                      {zipStatus === "failed" && (
                        <span className="text-[12px] text-[#D7263D]">
                          {t("filters.zipError")}
                        </span>
                      )}
                    </form>
                  )}
                </div>
              )}
            </div>

            {/* Results count */}
            {hasActiveFilters && visible.length > 0 && (
              <p className="text-[13px] text-[#7A7A7A] mb-4">
                {t("filters.resultsCount", { count: visible.length })}
              </p>
            )}

            {/* Grid */}
            {visible.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {visible.map((merchant) => (
                  // Wrapper de medición: impresión (IntersectionObserver 0.5,
                  // una vez por sesión) + click-through. onClick en bubble: el
                  // corazón de favorito corta la propagación y no cuenta; la
                  // navegación del Link NO se bloquea (solo se encola).
                  <div
                    key={merchant.id}
                    data-location-id={merchant.id}
                    ref={observeCard}
                    onClick={() => trackCardClick(merchant.id, "grid")}
                  >
                    <MerchantCard
                      merchant={merchant}
                      linkBase={linkBase}
                      isFavorite={favorites.has(merchant.id)}
                      onToggleFavorite={toggleFavorite}
                      matchedDish={
                        searchQuery
                          ? merchant.dish_names.find((d) => d.toLowerCase().includes(searchQuery))
                          : undefined
                      }
                      distanceMi={distances?.get(merchant.id) ?? null}
                    />
                  </div>
                ))}
              </div>
            ) : favoritesOnly && favorites.size === 0 ? (
              <div className="py-20 text-center flex flex-col items-center">
                <Heart className="w-9 h-9 text-[#D7263D]/30 mb-3" strokeWidth={1.5} aria-hidden />
                <p className="text-[#1A1A1A] text-sm font-semibold">{t("empty.noFavoritesTitle")}</p>
                <p className="text-[#7A7A7A] text-[13px] mt-1">{t("empty.noFavoritesBody")}</p>
                <button
                  onClick={() => setFavoritesOnly(false)}
                  className="mt-4 text-[#D7263D] text-sm font-semibold hover:underline"
                >
                  {t("empty.showAll")}
                </button>
              </div>
            ) : (
              <div className="py-20 text-center flex flex-col items-center">
                <SearchX className="w-9 h-9 text-[#C5C0B8] mb-3" strokeWidth={1.5} aria-hidden />
                <p className="text-[#1A1A1A] text-sm font-semibold">{t("empty.noResultsTitle")}</p>
                <p className="text-[#7A7A7A] text-[13px] mt-1">{t("empty.noResultsBody")}</p>
                <button
                  onClick={clearAll}
                  className="mt-4 text-[#D7263D] text-sm font-semibold hover:underline"
                >
                  {t("filters.clearFilters")}
                </button>
              </div>
            )}
          </>
        )}
      </section>

      <ConsumerBottomNav homeHref={homeHref} hasSession={!!account} />
    </div>
  )
}
